import React, { useState, useEffect } from "react";
import { Handshake, Shield, ScrollText, AlertTriangle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAdminAuth } from "../hooks/useAdminAuth";
import AdminHeader from "../components/admin/AdminHeader";

const COLORS = {
  bg: "#F4F4F0",
  bgAlt: "#ede5d6",
  ink: "#1a2330",
  inkSoft: "#4a5568",
  inkMuted: "#6b7280",
  dark: "#0e2030",
  card: "#ffffff",
  border: "rgba(15, 30, 45, 0.08)",
  borderStrong: "rgba(15, 30, 45, 0.16)",
};
const SERIF = "'Playfair Display', 'Source Serif Pro', Georgia, 'Times New Roman', serif";
const SANS = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Inter, system-ui, sans-serif";
const MONO = "'JetBrains Mono', 'Fira Code', monospace";

const API_BASE = process.env.REACT_APP_API_URL || "";

// The two prompts an admin can override. Order here drives the render order.
const PROMPTS = [
  {
    key: "panelist",
    title: "Panelist rulebook",
    subtitle:
      "The system prompt every AI judge on the panel receives before it reviews a dispute — persona, workflow, credibility factors, and the verdict format.",
  },
  {
    key: "judge",
    title: "Chief Judge rulebook",
    subtitle:
      "The system prompt the platform's Chief Judge receives to synthesize the panel's rulings into one final verdict on multi-provider cases.",
  },
];

export default function AdminPrompts() {
  const navigate = useNavigate();

  // Authorization header for /api/ai/admin/* — either a Basic Auth session
  // (sessionStorage-backed) or auto-detected via the current user's JWT if
  // they're an is_admin account. See useAdminAuth.
  const {
    authHeader,
    resolving: resolvingAuth,
    loginUser,
    setLoginUser,
    loginPass,
    setLoginPass,
    loginError,
    handleLogin,
    handleAdminLogout,
    handleUnauthorized,
  } = useAdminAuth("/api/ai/admin/prompts");

  const [loaded, setLoaded] = useState(false);
  const [defaults, setDefaults] = useState({ panelist: "", judge: "" });
  const [drafts, setDrafts] = useState({ panelist: "", judge: "" });
  const [isDefault, setIsDefault] = useState({ panelist: true, judge: true });
  const [saving, setSaving] = useState({ panelist: false, judge: false });
  const [saved, setSaved] = useState({ panelist: false, judge: false });
  const [error, setError] = useState({ panelist: null, judge: null });

  useEffect(() => {
    if (!authHeader) return;
    fetch(`${API_BASE}/api/ai/admin/prompts`, { headers: { Authorization: authHeader } })
      .then((r) => {
        if (r.status === 401) {
          handleUnauthorized();
          return null;
        }
        return r.json();
      })
      .then((d) => {
        if (!d) return;
        setDefaults(d.defaults || { panelist: "", judge: "" });
        setDrafts({ panelist: d.panelist || "", judge: d.judge || "" });
        setIsDefault({ panelist: !!d.panelistIsDefault, judge: !!d.judgeIsDefault });
        setLoaded(true);
      })
      .catch(() => {});
  }, [authHeader]);

  const savePrompt = async (key) => {
    setSaving((s) => ({ ...s, [key]: true }));
    setError((s) => ({ ...s, [key]: null }));
    setSaved((s) => ({ ...s, [key]: false }));
    try {
      const res = await fetch(`${API_BASE}/api/ai/admin/prompts`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: authHeader },
        body: JSON.stringify({ [key]: drafts[key] }),
      });
      if (res.status === 401) {
        handleUnauthorized();
        return;
      }
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setDrafts((d) => ({ ...d, [key]: data[key] ?? d[key] }));
      setIsDefault((s) => ({ ...s, [key]: !!data[`${key}IsDefault`] }));
      setSaved((s) => ({ ...s, [key]: true }));
      setTimeout(() => setSaved((s) => ({ ...s, [key]: false })), 2500);
    } catch (err) {
      setError((s) => ({ ...s, [key]: err.message }));
    } finally {
      setSaving((s) => ({ ...s, [key]: false }));
    }
  };

  const resetPrompt = (key) => {
    setDrafts((d) => ({ ...d, [key]: defaults[key] || "" }));
    setSaved((s) => ({ ...s, [key]: false }));
    setError((s) => ({ ...s, [key]: null }));
  };

  // While a logged-in user's JWT might still resolve to an is_admin session,
  // hold off on the login form so it doesn't flash before auto-login lands.
  if (resolvingAuth) {
    return (
      <div style={styles.loginRoot}>
        <div style={styles.loginCard}>
          <div style={styles.loginBrand}>
            <Handshake size={26} strokeWidth={1.75} />
            <span style={styles.logo}>disputef<em style={styles.logoItalic}>AI</em></span>
          </div>
          <p style={styles.loginSubtitle}>Checking your session…</p>
        </div>
      </div>
    );
  }

  // ── Login screen ───────────────────────────────────────────────────────────
  if (!authHeader) {
    return (
      <div style={styles.loginRoot}>
        <div style={styles.loginCard}>
          <div style={styles.loginBrand}>
            <Handshake size={26} strokeWidth={1.75} />
            <span style={styles.logo}>disputef<em style={styles.logoItalic}>AI</em></span>
          </div>
          <div style={styles.adminPill}>
            <Shield size={12} strokeWidth={2} style={{ verticalAlign: "middle", marginRight: 4 }} />
            Admin sign-in
          </div>
          <h1 style={styles.loginTitle}>Restricted area</h1>
          <p style={styles.loginSubtitle}>
            This panel uses its own credentials. They are independent from your normal account.
          </p>
          <form onSubmit={handleLogin} style={styles.loginForm}>
            <input
              type="text"
              autoComplete="username"
              placeholder="Admin username"
              value={loginUser}
              onChange={(e) => setLoginUser(e.target.value)}
              style={styles.loginInput}
              autoFocus
            />
            <input
              type="password"
              autoComplete="current-password"
              placeholder="Admin password"
              value={loginPass}
              onChange={(e) => setLoginPass(e.target.value)}
              style={styles.loginInput}
            />
            {loginError && <div style={styles.loginErr}>{loginError}</div>}
            <button type="submit" style={styles.loginBtn}>Enter admin panel</button>
            <button
              type="button"
              onClick={() => navigate("/submit-dispute")}
              style={styles.loginGhost}
            >Back to dashboard</button>
          </form>
        </div>
      </div>
    );
  }

  // ── Authenticated admin view ───────────────────────────────────────────────
  return (
    <div style={styles.root}>
      <AdminHeader active="prompts" onLogout={handleAdminLogout} />

      <main style={styles.main}>
        <div style={styles.pageHeader}>
          <div>
            <h1 style={styles.title}>Prompts (admin)</h1>
            <p style={styles.subtitle}>
              The AI rulebooks that govern how the panel and Chief Judge decide every dispute.
            </p>
          </div>
        </div>

        {!loaded ? (
          <div style={styles.judgeCardSub}>Loading…</div>
        ) : (
          PROMPTS.map(({ key, title, subtitle }) => {
            const draft = drafts[key] || "";
            const hasJsonFence = /```json/i.test(draft);
            return (
              <div key={key} style={styles.promptCard}>
                <div style={styles.promptHeader}>
                  <span style={styles.judgeIconWrap}>
                    <ScrollText size={18} strokeWidth={1.9} />
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={styles.promptTitleRow}>
                      <h2 style={styles.judgeCardTitle}>{title}</h2>
                      <span style={isDefault[key] ? styles.badgeDefault : styles.badgeCustom}>
                        {isDefault[key] ? "default" : "customized"}
                      </span>
                    </div>
                    <p style={styles.judgeCardSub}>{subtitle}</p>
                  </div>
                </div>

                <div style={styles.warnBanner}>
                  <AlertTriangle size={14} strokeWidth={2} style={{ flexShrink: 0, marginTop: 1 }} />
                  <span>
                    This changes how every future dispute is judged, starting with the next scheduled panel
                    run — no deploy needed. The prompt must still end with a fenced <code>```json</code> block
                    (see the default for the exact shape) or verdicts will fail to parse and the case will
                    retry/stall.
                    {!hasJsonFence && (
                      <strong style={{ display: "block", marginTop: 4, color: "#991b1b" }}>
                        ⚠ No ```json fence found in the current draft — double-check before saving.
                      </strong>
                    )}
                  </span>
                </div>

                <textarea
                  style={styles.promptTextarea}
                  value={draft}
                  onChange={(e) => {
                    const v = e.target.value;
                    setDrafts((d) => ({ ...d, [key]: v }));
                    setSaved((s) => ({ ...s, [key]: false }));
                  }}
                  rows={20}
                  spellCheck={false}
                />
                <div style={styles.promptFooter}>
                  <span style={styles.charCount}>{draft.length.toLocaleString()} characters</span>
                  <div style={styles.judgeControls}>
                    <button
                      type="button"
                      onClick={() => resetPrompt(key)}
                      style={styles.modelsValidateBtn}
                    >Reset to default</button>
                    <button
                      type="button"
                      onClick={() => savePrompt(key)}
                      disabled={saving[key]}
                      style={{ ...styles.judgeSaveBtn, ...(saving[key] ? styles.judgeSaveBtnDisabled : {}) }}
                    >
                      {saving[key] ? "Saving…" : "Save"}
                    </button>
                    {saved[key] && <span style={styles.judgeSavedMsg}>Saved ✓</span>}
                    {error[key] && <span style={styles.judgeErrMsg}>{error[key]}</span>}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </main>
    </div>
  );
}

const styles = {
  root: { minHeight: "100vh", background: COLORS.bg, color: COLORS.ink, fontFamily: SANS, fontSize: 14 },

  // ── Login screen ─────────────────────────────────────────────────────────
  loginRoot: {
    minHeight: "100vh", background: COLORS.bg, color: COLORS.ink, fontFamily: SANS,
    display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
  },
  loginCard: {
    width: "100%", maxWidth: 420, background: COLORS.card,
    border: `1px solid ${COLORS.border}`, borderRadius: 12,
    padding: "32px 28px", boxShadow: "0 10px 30px rgba(15, 30, 45, 0.08)",
  },
  loginBrand: { display: "inline-flex", alignItems: "center", gap: 8, color: COLORS.ink, marginBottom: 16 },
  adminPill: {
    display: "inline-block", background: COLORS.dark, color: "#fff",
    padding: "3px 10px", borderRadius: 999, fontSize: 11, fontWeight: 700,
    letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 12,
  },
  loginTitle: {
    fontFamily: SERIF, fontSize: 26, fontWeight: 600, margin: "0 0 8px 0",
    letterSpacing: "-0.3px", color: COLORS.ink,
  },
  loginSubtitle: { fontSize: 13, color: COLORS.inkMuted, margin: "0 0 20px 0", lineHeight: 1.5 },
  loginForm: { display: "flex", flexDirection: "column", gap: 12 },
  loginInput: {
    width: "100%", boxSizing: "border-box",
    background: COLORS.bg, color: COLORS.ink,
    border: `1px solid ${COLORS.borderStrong}`, borderRadius: 8,
    padding: "10px 12px", fontSize: 14, fontFamily: SANS, outline: "none",
  },
  loginErr: {
    background: "#fef2f2", color: "#991b1b",
    border: "1px solid #fecaca", borderRadius: 6,
    padding: "8px 10px", fontSize: 12,
  },
  loginBtn: {
    background: COLORS.dark, color: "#fff", border: "none", borderRadius: 8,
    padding: "11px 16px", fontSize: 14, fontWeight: 600, fontFamily: SANS,
    cursor: "pointer",
  },
  loginGhost: {
    background: "transparent", color: COLORS.inkSoft,
    border: `1px solid ${COLORS.border}`, borderRadius: 8,
    padding: "9px 16px", fontSize: 13, fontFamily: SANS, cursor: "pointer",
  },

  // ── Authenticated layout ─────────────────────────────────────────────────
  logo: { fontFamily: SERIF, fontSize: 22, fontWeight: 600, letterSpacing: "-0.3px" },
  logoItalic: { fontStyle: "italic", fontWeight: 600 },

  main: { padding: "32px clamp(16px, 4vw, 48px)", maxWidth: 1280, margin: "0 auto" },

  pageHeader: {
    display: "flex", justifyContent: "space-between", alignItems: "flex-end",
    flexWrap: "wrap", gap: 16, marginBottom: 24,
  },
  title: { fontFamily: SERIF, fontSize: 32, fontWeight: 600, margin: 0, letterSpacing: "-0.5px" },
  subtitle: { fontSize: 13, color: COLORS.inkMuted, margin: "6px 0 0 0" },

  // ── Prompt cards ─────────────────────────────────────────────────────────
  promptCard: {
    background: COLORS.card, border: `1px solid ${COLORS.border}`,
    borderRadius: 12, padding: "18px 20px", marginBottom: 20,
    display: "flex", flexDirection: "column", gap: 12,
  },
  promptHeader: { display: "flex", alignItems: "flex-start", gap: 14 },
  promptTitleRow: { display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" },
  judgeIconWrap: {
    width: 36, height: 36, flexShrink: 0, borderRadius: 8,
    background: COLORS.bgAlt, color: COLORS.dark,
    display: "flex", alignItems: "center", justifyContent: "center",
  },
  judgeCardTitle: { fontFamily: SERIF, fontSize: 18, fontWeight: 600, margin: 0, letterSpacing: "-0.2px" },
  judgeCardSub: { fontSize: 12, color: COLORS.inkMuted, margin: "3px 0 0 0" },
  badgeDefault: {
    fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4,
    color: COLORS.inkMuted, background: COLORS.bgAlt, borderRadius: 999, padding: "2px 9px",
  },
  badgeCustom: {
    fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4,
    color: "#8a5a12", background: "#fdf1dd", borderRadius: 999, padding: "2px 9px",
  },
  warnBanner: {
    display: "flex", gap: 8, alignItems: "flex-start",
    background: "#fffaf0", border: "1px solid #f6d99b", borderRadius: 8,
    padding: "10px 12px", fontSize: 12, color: "#7a5410", lineHeight: 1.5,
  },
  promptTextarea: {
    width: "100%", boxSizing: "border-box", resize: "vertical",
    background: COLORS.bg, border: `1px solid ${COLORS.borderStrong}`, borderRadius: 8,
    padding: "10px 12px", fontSize: 12.5, fontFamily: MONO, color: COLORS.ink,
    outline: "none", lineHeight: 1.6,
  },
  promptFooter: {
    display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10,
  },
  charCount: { fontSize: 11, color: COLORS.inkMuted },
  judgeControls: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
  judgeSaveBtn: {
    background: COLORS.dark, color: "#fff", border: "none", borderRadius: 8,
    padding: "9px 16px", fontSize: 13, fontWeight: 600, fontFamily: SANS, cursor: "pointer",
  },
  judgeSaveBtnDisabled: { background: "#9aa3ad", cursor: "not-allowed" },
  judgeSavedMsg: { fontSize: 13, color: "#1f6f43", fontWeight: 600 },
  judgeErrMsg: { fontSize: 12, color: "#991b1b" },
  modelsValidateBtn: {
    background: "transparent", color: COLORS.dark, border: `1px solid ${COLORS.borderStrong}`,
    borderRadius: 8, padding: "9px 16px", fontSize: 13, fontWeight: 600,
    fontFamily: SANS, cursor: "pointer",
  },
};
